import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Award, Home, Briefcase, Activity, Calendar, AlertTriangle, Loader2, Flame, BookOpen, Target, Trophy } from 'lucide-react';

const SOBRIETY_MILESTONES = [
  { days: 30, label: '30-Day', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { days: 60, label: '60-Day', color: 'bg-teal-100 text-teal-700 border-teal-300' },
  { days: 90, label: '90-Day', color: 'bg-cyan-100 text-cyan-700 border-cyan-300' },
  { days: 180, label: '6-Month', color: 'bg-violet-100 text-violet-700 border-violet-300' },
  { days: 365, label: '1-Year', color: 'bg-amber-100 text-amber-700 border-amber-300' },
];

export default function OutcomeMetrics({ participantId }) {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!participantId) return;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/outcome-metrics/dashboard/${participantId}`);
        setData(res.data);
      } catch { setData(null); }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participantId]);

  if (loading) return <div className="flex justify-center p-8"><Loader2 className="animate-spin text-teal-600" size={22} /></div>;
  if (!data) return <p className="text-sm text-muted-foreground text-center p-4">Could not load outcome metrics.</p>;

  const s = data.sobriety;
  const h = data.housing;
  const e = data.engagement;
  const r = data.risk_indicators;

  const milestonesAchieved = SOBRIETY_MILESTONES.filter(m => s.current_streak_days >= m.days);

  return (
    <div className="space-y-4" data-testid="outcome-metrics">
      {/* Hero — Sobriety streak */}
      <Card className="border-0 shadow-md rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
        <CardContent className="p-5">
          <div className="flex items-center gap-3">
            <Flame size={36} className="text-amber-200" />
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wide text-emerald-100">Current Sobriety Streak</p>
              <p className="text-4xl font-bold">{s.current_streak_days}<span className="text-lg font-medium text-emerald-100"> days</span></p>
              {s.avg_mood_14d != null && <p className="text-sm text-emerald-100 mt-0.5">Avg mood last 14d: {s.avg_mood_14d}/10</p>}
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold">{data.tenure.enrollment_months}<span className="text-sm font-medium text-emerald-100">mo</span></p>
              <p className="text-xs text-emerald-100">in program</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Milestones */}
      {milestonesAchieved.length > 0 && (
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2"><Trophy size={14} className="text-amber-500" /> Sobriety Milestones</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {SOBRIETY_MILESTONES.map(m => {
                const achieved = s.current_streak_days >= m.days;
                return (
                  <Badge key={m.days} className={`${achieved ? m.color : 'bg-slate-50 text-slate-400 border-slate-200'} border text-xs px-2.5 py-1`}>
                    {achieved ? '🏅 ' : ''}{m.label}
                  </Badge>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className={`border-0 shadow-sm rounded-2xl ${h.currently_housed ? 'bg-sky-50' : 'bg-slate-50'}`} data-testid="metric-housing">
          <CardContent className="p-3.5">
            <Home className={h.currently_housed ? 'text-sky-600' : 'text-slate-400'} size={18} />
            <p className="text-xl font-bold mt-1.5">{h.currently_housed ? `${h.current_tenure_days}d` : '—'}</p>
            <p className="text-[11px] text-slate-600">{h.currently_housed ? `Housed @ ${h.current_property || 'property'}` : (h.previously_completed ? 'Completed housing' : 'Not housed')}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-2xl bg-amber-50" data-testid="metric-employment">
          <CardContent className="p-3.5">
            <Briefcase className="text-amber-600" size={18} />
            <p className="text-xl font-bold mt-1.5 capitalize">{(data.employment.status || 'unknown').replace('_', ' ')}</p>
            <p className="text-[11px] text-slate-600">Employment</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-2xl bg-violet-50" data-testid="metric-engagement">
          <CardContent className="p-3.5">
            <BookOpen className="text-violet-600" size={18} />
            <p className="text-xl font-bold mt-1.5">{e.classes_attended}</p>
            <p className="text-[11px] text-slate-600">Classes attended</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-2xl bg-emerald-50" data-testid="metric-goals">
          <CardContent className="p-3.5">
            <Target className="text-emerald-600" size={18} />
            <p className="text-xl font-bold mt-1.5">{e.goals_completed}<span className="text-sm text-slate-500">/{e.goals_total}</span></p>
            <p className="text-[11px] text-slate-600">Goals completed</p>
          </CardContent>
        </Card>
      </div>

      {/* Engagement detail bar */}
      <Card className="border-0 shadow-sm rounded-2xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2"><Activity size={14} /> Engagement Profile</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 pt-1">
          <div>
            <div className="flex justify-between text-xs mb-1"><span>Reflections completed</span><span className="text-slate-500">{e.reflections_completed} of {e.classes_attended}</span></div>
            <Progress value={(e.reflections_completed / Math.max(e.classes_attended, 1)) * 100} className="h-2" />
          </div>
          <div>
            <div className="flex justify-between text-xs mb-1"><span>Goals completion rate</span><span className="text-slate-500">{e.goals_total > 0 ? Math.round(e.goals_completed / e.goals_total * 100) : 0}%</span></div>
            <Progress value={e.goals_total > 0 ? (e.goals_completed / e.goals_total) * 100 : 0} className="h-2" />
          </div>
          <div className="flex items-center justify-between text-xs pt-1">
            <span className="flex items-center gap-1 text-amber-700"><Award size={12} /> Achievements earned</span>
            <span className="font-semibold">{e.achievements_earned}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1 text-slate-600"><Calendar size={12} /> Daily check-ins logged</span>
            <span className="font-semibold">{s.total_checkins}</span>
          </div>
        </CardContent>
      </Card>

      {/* Risk indicators */}
      {(r.incidents_last_90d > 0 || r.mood_concern) && (
        <Card className="border-0 shadow-sm rounded-2xl border-l-4 border-l-amber-400 bg-amber-50/60">
          <CardContent className="p-3.5 space-y-1">
            <p className="text-xs font-semibold text-amber-800 flex items-center gap-1"><AlertTriangle size={12} /> Watch Items</p>
            {r.incidents_last_90d > 0 && <p className="text-xs text-amber-700">{r.incidents_last_90d} incident{r.incidents_last_90d !== 1 ? 's' : ''} reported in last 90 days</p>}
            {r.mood_concern && <p className="text-xs text-amber-700">Mood trending low (avg under 4/10) — recommend peer check-in</p>}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
